import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
  Divider,
  Paper,
  alpha,
  Skeleton,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import StorageIcon from "@mui/icons-material/Storage";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useHistory } from "react-router-dom";
import { slideInUp } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";

const PricingSection = ({ theme }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const history = useHistory();
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = async () => {
    const res = await hitAxios({
      path: "/api/plans/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success) setLoading(false);
    setPlans(res.data.data.filter((p) => p.status === "active"));
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const planColors = [
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.secondary?.main || theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  // Mark the middle plan (or highest price) as popular
  const popularIndex = plans.length > 1 ? Math.floor(plans.length / 2) : 0;

  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 10, md: 14 },
        background: isDark
          ? alpha(theme.palette.background.paper, 0.3)
          : alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Container maxWidth="lg">
        {/* ── Header ── */}
        <Box textAlign="center" mb={8}>
          <Chip
            label={lang?.pricingChip || "Simple Pricing"}
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.03em",
              mb: 2,
            }}
          >
            {lang?.pricingTitle || "Plans for Every Need"}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 460, mx: "auto", lineHeight: 1.7 }}
          >
            {lang?.pricingSubtitle ||
              "Start free, upgrade when you need more. No hidden fees, no surprises."}
          </Typography>
        </Box>

        {/* ── Skeletons while loading ── */}
        {loading && (
          <Grid container spacing={3}>
            {[0, 1, 2].map((i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <Skeleton
                  variant="rounded"
                  height={380}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Plan Cards ── */}
        {!loading && (
          <Grid container spacing={3} alignItems="stretch">
            {plans.map((plan, i) => {
              const color = planColors[i % planColors.length];
              const isPopular = i === popularIndex && plans.length > 1;
              const isFree = parseFloat(plan.price) === 0;

              return (
                <Grid
                  size={{ xs: 12, md: plans.length === 1 ? 6 : 4 }}
                  key={plan.id}
                  sx={{ display: "flex", mx: plans.length === 1 ? "auto" : 0 }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      animation: `${slideInUp} 0.6s ease both`,
                      animationDelay: `${i * 0.15}s`,
                      display: "flex",
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden",
                        borderColor: isPopular ? color : alpha(color, 0.15),
                        borderWidth: isPopular ? 2 : 1,
                        position: "relative",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: `0 20px 50px ${alpha(color, 0.18)}`,
                        },
                      }}
                    >
                      {/* Popular ribbon */}
                      {isPopular && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: -28,
                            transform: "rotate(45deg)",
                            bgcolor: color,
                            color: "#fff",
                            px: 4,
                            py: 0.4,
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {lang?.pricingPopularBadge || "POPULAR"}
                        </Box>
                      )}

                      {/* Top accent bar */}
                      <Box
                        sx={{
                          height: 4,
                          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
                        }}
                      />

                      <Box
                        sx={{
                          p: 3,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Plan name */}
                        <Typography
                          variant="overline"
                          fontWeight={700}
                          sx={{
                            color,
                            fontSize: "0.7rem",
                            letterSpacing: "0.1em",
                          }}
                        >
                          {plan.name}
                        </Typography>

                        {/* Price */}
                        <Stack
                          direction="row"
                          alignItems="flex-end"
                          spacing={0.5}
                          mb={0.5}
                          mt={0.5}
                        >
                          <Typography
                            variant="h2"
                            fontWeight={900}
                            sx={{
                              fontSize: "2.8rem",
                              letterSpacing: "-0.04em",
                              color,
                              lineHeight: 1,
                            }}
                          >
                            {isFree
                              ? "Free"
                              : `$${parseFloat(plan.price).toFixed(0)}`}
                          </Typography>
                          {!isFree && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              mb={0.5}
                            >
                              / {plan.duration_days} days
                            </Typography>
                          )}
                        </Stack>

                        {/* Description */}
                        {plan.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              fontSize: "0.8rem",
                              lineHeight: 1.5,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        )}

                        <Divider sx={{ my: 2.5 }} />

                        {/* Features */}
                        <Stack spacing={1.25} sx={{ flex: 1, mb: 3 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <StorageIcon sx={{ fontSize: 16, color }} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {plan.max_storage_gb} GB Cloud Storage
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <CloudDownloadIcon sx={{ fontSize: 16, color }} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {plan.max_downloads} Downloads
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <CalendarTodayIcon sx={{ fontSize: 16, color }} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {isFree
                                ? "Lifetime Access"
                                : `${plan.duration_days} Days Access`}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* CTA */}
                        <Button
                          fullWidth
                          variant={isPopular ? "contained" : "outlined"}
                          onClick={() => history.push("/user?page=my-plan")}
                          sx={{
                            borderRadius: 2,
                            py: 1.25,
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            ...(isPopular && {
                              background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
                              boxShadow: `0 6px 20px ${alpha(color, 0.4)}`,
                              "&:hover": {
                                boxShadow: `0 10px 28px ${alpha(color, 0.5)}`,
                              },
                            }),
                            ...(!isPopular && {
                              borderColor: alpha(color, 0.4),
                              color,
                              "&:hover": {
                                borderColor: color,
                                bgcolor: alpha(color, 0.06),
                              },
                            }),
                          }}
                        >
                          {isFree
                            ? lang?.pricingStarterCta || "Get Started Free"
                            : lang?.pricingProCta || "Get Started"}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ── Empty state ── */}
        {!loading && plans.length === 0 && (
          <Box textAlign="center" py={6}>
            <Typography color="text.secondary">
              No plans available at the moment.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PricingSection;
